import { Alert, AlertType, dom, events } from '@omegagrid/core';
import { msg } from '@omegagrid/localize';
import rp, { AppComponent, GridEditor, Application, ParsedComponentId, EditorContainer, formatAccountingGroup } from '@reporting/rp-app-host';
import { SettingsGridEditorAdapter, SettingsScope, UserGridEditorAdapter, UserGroupGridEditorAdapter } from './adapters';
import { UserFormContainer } from './components';
import { CallerContext, a } from './types';
import { AccountingGroup } from "@reporting/rp-types/core";
import { GroupTreeAdapter } from "@reporting/rp-mf-accounting"
import { User } from '@reporting/rp-types/core';
import * as access from './factories.access';
import * as timesheet from './factories.timesheet';

const createUsersGridEditor = async (app: Application, componentId: ParsedComponentId) => {
	const superAdmin = (componentId.ids[1] as CallerContext) === 'super';
	const g = componentId.ids[2] && parseInt(componentId.ids[2]) ?
		parseInt(componentId.ids[2]) :
		(!superAdmin ? rp.accountingProvider.accounting.id_accounting_group : null);
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new UserGridEditorAdapter(
		g ? await rp.ms('accounting').get<AccountingGroup>(`groups/${g}`) : null,
		superAdmin
	);
	return component;
}

const createUsersGridEditorContainer = async (app: Application, componentId: ParsedComponentId) => {
	const g = componentId.ids[1] || null;
	const group: AccountingGroup | null = g ? await rp.ms('accounting').get<AccountingGroup>(`groups/${g}`) : null;

	const container = dom.createElement<EditorContainer>('rp-editor-container', {
		items: [{
			id: 'group',
			width: 250,
			value: group ? [[group.id_group, 0, 1, {v: formatAccountingGroup(group)}]] : [],
			type: 'dropdown',
			caption: msg('Group'),
			adapter: new GroupTreeAdapter({ selectMode: 'single', autoClose: true, checkboxes: false, search: true }, true),
		}],
		createComponent: id => rp.host.createComponent(id),
		// triggered when toolbar dropdown values change
		updateComponentId: (values) => {
			const key = values['group'] ?? g;
			return `users-users-super${key ? `-${key}` : ''}`;
		}
	});

	container.addEventListener('change', (e: events.ChangeEvent) => {
		const key = e.value['group'];
		rp.host.updateComponent(
			container.componentId,
			{ id: `users-usersContainer${key ? `-${key}` : ''}`, title: msg('Users') }
		);
	});

	return container;
}

const createUserFormContainer = async (app: Application, componentId: ParsedComponentId) => {
	const caller = componentId.ids[1] as CallerContext;
	const group = parseInt(componentId.ids[2]) ? await rp.ms('accounting').get<AccountingGroup>(`groups/${parseInt(componentId.ids[2])}`) : null;
	const user = componentId.ids[3] ? await rp.ms('users').get<User>(`/${componentId.ids[3]}`) : null;
	const component = dom.createElement<UserFormContainer>('rp-users-userform-container', { group, caller, user });
	component.addEventListener('save', () => {
		if (caller === 'user' ) {
			const dialog = rp.host.bucket.dialogs?.items?.get(`${componentId.type}-${componentId.ids.join('-')}`);
			if (dialog) dialog.close();
		} else if (caller === 'admin') {
			const id = 'users-users';
			if (app.host.components.has(id)) (app.host.components.get(id) as GridEditor).refresh();
		} else if (group) {
			const id = `users-usersContainer-${group.id_group}`;
			if (app.host.components.has(id)) {
				const container = app.host.components.get(id) as EditorContainer;
				(container.body.firstChild as GridEditor).refresh();
			}
		}
	});
	return component as unknown as AppComponent;
}

const createUserGroupGridEditor = async (app: Application, componentId: ParsedComponentId) => {
	const g = componentId.ids[1] && parseInt(componentId.ids[1]) ? parseInt(componentId.ids[1]) : null;
	const component = dom.createElement<GridEditor>('rp-editor-grid');
	component.adapter = new UserGroupGridEditorAdapter(g);
	return component;
}

const createUserGroupGridEditorContainer = async (app: Application, componentId: ParsedComponentId) => {
	const g = componentId.ids[1] ? componentId.ids[1] : null;
	const group: AccountingGroup | null = g ? await rp.ms('accounting').get<AccountingGroup>(`groups/${g}`) : null;

	const container = dom.createElement<EditorContainer>('rp-editor-container', {
		items: [{
			id: 'group',
			width: 250,
			value: group ? [[group.id_group, 0, 1, {v: formatAccountingGroup(group)}]] : [],
			type: 'dropdown',
			caption: msg('Group'),
			adapter: new GroupTreeAdapter({ selectMode: 'single', autoClose: true, checkboxes: false, search: true }),
		}],
		createComponent: id => rp.host.createComponent(id),
		// triggered when toolbar dropdown values change
		updateComponentId: (values) => {
			const key = values['group'] ?? g;
			if (!key) return `users-alert-Select group`;
			return `users-userGroups-${key}`;
		}
	});

	container.addEventListener('change', (e: events.ChangeEvent) => {
		const key = e.value['group'];
		if (!key) return;
		rp.host.updateComponent(
			container.componentId,
			{ id: `users-userGroupsContainer${key}`, title: msg('User groups') }
		);
	});

	return container;
}

const createSettingsGridEditor = async (app: Application, componentId: ParsedComponentId) => {
	// users-settings                     -> personal (user) editor (your own settings)
	// users-settings-user-<user_name>    -> personal editor for another user (admin)
	// users-settings-accounting-<id>     -> accounting-scope editor for accounting <id>
	// users-settings-group-<id>          -> group-scope editor for group <id>
	// users-settings-role-<id>           -> role-scope editor for access role <id>
	const scope = (componentId.ids[1] as SettingsScope) || 'user';
	// the user scope targets a username (string); the others target a numeric id
	const scopeId = componentId.ids[2]
		? (scope === 'user' ? componentId.ids[2] : parseInt(componentId.ids[2]))
		: null;
	return dom.createElement<GridEditor>('rp-editor-grid', {
		adapter: new SettingsGridEditorAdapter(scope, scopeId),
	});
};

const createAlert = async (app: Application, componentId: ParsedComponentId) =>
	dom.createElement<Alert>('og-alert', {
		closable: false,
		icon: 'circle-info',
		style: { marginTop: '10px' },
		text: msg(componentId.ids[1]),
		type: componentId.ids[2] as AlertType || 'info',
	}) as unknown as AppComponent;

export const componentFactories = new Map<string, (app: Application, componentId: ParsedComponentId) => AppComponent|Promise<AppComponent>>([
	['users', createUsersGridEditor],
	['usersContainer', createUsersGridEditorContainer],
	['entityAccess', access.createEntityAccessGridEditor],
	['entityAccessContainer', access.createEntityAccessGridEditorContainer],
	['entityUsers', access.createEntityUsersGridEditor],
	['userForm', createUserFormContainer],
	['userGroups', createUserGroupGridEditor],
	['userGroupsContainer', createUserGroupGridEditorContainer],
	['userAccess', access.createUserAccessGridEditor],
	['userAccessContainer', access.createUserAccessGridEditorContainer],
	['userAccessSuperContainer', access.createUserAccessSuperGridEditorContainer],
	['alert', createAlert],
	['settings', createSettingsGridEditor],
	['timesheet', timesheet.createTimesheetGrid],
	['timesheetContainer', timesheet.createTimesheetGridContainer],
]);
